import { defineStore } from 'pinia';
import { Firestore, collection, doc, getDoc, getDocs, setDoc, deleteDoc, query, orderBy, CollectionReference } from 'firebase/firestore';
import { useUserStore } from './user';
import { type FeatureOptions, type Feature, FeatureType } from '../models/feature';
import { type Effect } from '~/models/effect';
import { type BlueprintFeatureSelection } from '../models/blueprint';
import { generateBlueprintFeatureId } from '~/utils/idGenerator';

export interface BluePrintsStateInterface  {
	character: BlueprintFeatureSelection[];
	loading: boolean;
  }
  

export const useBlueprintStore = defineStore('BlueprintStore', {
	state: (): BluePrintsStateInterface => ({
		character: [],
		loading: false,
	}),

  getters: {
	getCharacterFeaturesForRank: (state) => {
		return (rank: number): BlueprintFeatureSelection => {
			const featuresByRank = state.character; // Accessing the character features
			if (rank < 0 || rank >= featuresByRank.length) {
				throw new Error("Invalid rank specified"); // Handle invalid rank
			}
			return featuresByRank[rank]; // Return features for the specified rank
		};
	},
	getcharacterFeaturesToRank: (state) => {
		return (rank: number): BlueprintFeatureSelection[] => {
			const featuresByRank =  state.character; // Accessing the character features
			if (rank < 0 || rank >= featuresByRank.length) {
				throw new Error("Invalid rank specified"); // Handle invalid rank
			}
	
			// Concatenate features from all ranks up to the specified rank
			let allFeaturesUpToRank: BlueprintFeatureSelection[] = [];
			for (let i = 0; i <= rank; i++) {
				allFeaturesUpToRank = allFeaturesUpToRank.concat(featuresByRank[i]);
			}
			return allFeaturesUpToRank;
		};
	},
	getCountOfCharacterFeaturesByTypeAtRank: (state) => {
		return (featureType: FeatureType, rank: number): number => {
			const featuresByRank =  state.character; // Accessing the character features
			if (rank < 0 || rank >= featuresByRank.length) {
				throw new Error("Invalid rank specified"); // Handle invalid rank
			}
			// Concatenate features from all ranks up to the specified rank
			let featuresAtRank: BlueprintFeatureSelection = featuresByRank[rank];

			// Define a recursive function to count matching features
			const countMatchingFeatures = (features: Feature[]): number => {
				return features.reduce((count, feature) => {
					// Count the feature if it matches the type
					const match = feature.type === featureType ? 1 : 0;
	
					// Count matches in subfeatures
					return count + match + countMatchingFeatures(feature.subFeatures);
				}, 0);
			};
	
			// Count matching items in all features and subfeatures at the specified rank
			return countMatchingFeatures(featuresAtRank);
		};
	},
    getFeatureOptions: (state) => {
		return (feature: Feature): FeatureOptions => {
			return [];
		}
    }    
  },

	actions: {
		async fetchAll(force: boolean = false) {
			const userStore = useUserStore();
			if (userStore.isLoggedIn && (this.character.length == 0 || force)) {
				this.loading = true;
				const { $firestore } = useNuxtApp();
				const allRanksFeatures: BlueprintFeatureSelection[] = [];
				const subFeatureIdsSet = new Set<string>();
		
				for (let rank = 0; rank <= 10; rank++) {
					try {
						const nodesRef = collection($firestore as Firestore, `/blueprints/character/${rank}`);
						const rankFeatures = await this.loadRankFeatures(nodesRef);
		
						// Collect all subFeature IDs (including nested)
						rankFeatures.forEach(feature => {
							feature.subFeatureIds.forEach(id => subFeatureIdsSet.add(id));
							for (const sub of feature.subFeatures) {
								if (sub.subFeatureIds?.length) {
									sub.subFeatureIds.forEach(id => subFeatureIdsSet.add(id));
								}
							}
						});
		
						allRanksFeatures[rank] = rankFeatures; // Using rank as index
					} catch (error) {
						console.error(`Error loading features for rank ${rank}:`, error);
						allRanksFeatures[rank] = []; // Assign an empty array for ranks with no data or errors
					}
				}
		
				// Filter out sub-features from allRanksFeatures
				this.character = allRanksFeatures.map(rankFeatures =>
					rankFeatures.filter(feature => !subFeatureIdsSet.has(feature.id as string))
				);
				this.loading = false;
			}
		},
		async upsert(dat: BlueprintFeatureSelection[]) {
			console.log(`Blueprint Store upsertItem: character`);
			const userStore = useUserStore();
			if (userStore.isLoggedIn) {
				const { $firestore } = useNuxtApp();
				for (const [rank, featureArray] of dat.entries()) {
					const nodesRef = collection($firestore as Firestore, `/blueprints/character/${rank.toString()}`);
					for (const [innerIndex, feature] of featureArray.entries()) {
						await this.upsertFeatureWithSubFeatures(nodesRef, feature);
					}
				}
			}
		},
		async upsertFeatureWithSubFeatures(nodesRef: CollectionReference, feature: Feature) {
			console.log(`Upserting feature "${feature.name}" with ${feature.subFeatures.length} sub-features`);

			// Upsert sub-features first and collect their IDs
			const updatedSubFeatureIds = [];
			for (const subFeature of feature.subFeatures) {
				const subFeatureId = await this.upsertFeature(nodesRef, subFeature);
				updatedSubFeatureIds.push(subFeatureId);
				console.log(`  - Upserted sub-feature "${subFeature.name}" with ID: ${subFeatureId}`);
			}
			// Update the parent feature's subFeatures array with new IDs
			feature.subFeatureIds = updatedSubFeatureIds;
			feature.subFeatures = [];

			console.log(`Parent feature "${feature.name}" now has subFeatureIds:`, feature.subFeatureIds);

			// Now upsert the parent feature
			await this.upsertFeature(nodesRef, feature);
			console.log(`Saved parent feature "${feature.name}" to Firestore`);
		},
		async upsertFeature(nodesRef: CollectionReference, feature: Feature): Promise<string> {
			let nodeRef;
			if (feature.id === '-1' || feature.id === undefined || !feature.id) {
				// Extract blueprint type and rank from collection path
				// Path format: blueprints/character/0
				const pathParts = nodesRef.path.split('/');
				const blueprintType = pathParts[1] || 'character'; // Default to 'character'
				const rank = parseInt(pathParts[2] || '0', 10);

				// Generate standardized ID: <blueprintType>-<rank>-<name>
				feature.id = generateBlueprintFeatureId(blueprintType, rank, feature.name);
				console.log(`Generated new feature ID: ${feature.id}`);
				nodeRef = doc(nodesRef, feature.id);
			} else {
				nodeRef = doc(nodesRef, feature.id);
				console.log(`Using existing feature.id: ${feature.id}`);
			}

			// Ensure internal ID matches document ID before saving
			// Strip undefined values — Firestore rejects them
			const featureToSave = JSON.parse(JSON.stringify({ ...feature, id: feature.id }));

			await setDoc(nodeRef, featureToSave)
				.catch((error) => {
					console.log('Error saving Blueprint: ', error);
				});

			return feature.id; // Return the ID of the feature
		},
		async deleteFeature(nodesRef: CollectionReference, featureId: string) {
			const nodeRef = doc(nodesRef, featureId);
			await deleteDoc(nodeRef)
				.catch((error) => {
					console.error('Error deleting feature:', error);
					throw error;
				});
			console.log(`Deleted feature with ID: ${featureId}`);
		},
		async getFeatureById(nodesRef: CollectionReference, featureId: string): Promise<Feature> {
			const docRef = doc(nodesRef, featureId);
			const docSnap = await getDoc(docRef);
		
			if (docSnap.exists()) {
				return docSnap.data() as Feature;
			} else {
				throw new Error(`Feature with ID ${featureId} not found`);
			}
		},
		async loadEffects(feature: Feature) {
			if (feature.effects && feature.effects.length > 0) return feature.effects;

			const effectStore = useEffectStore();

			// Bundle path: resolve from string IDs
			if (feature._effectRefIds?.length) {
				feature.effects = feature._effectRefIds
					.map(id => effectStore.effectById(id))
					.filter((e): e is Effect => e !== undefined);
				return feature.effects;
			}

			// Firestore path: resolve from DocumentReferences
			if (feature.effectRefs && feature.effectRefs.length > 0) {
				const effects = await effectStore.fetchEffectByRef(feature.effectRefs);
				feature.effects = effects;
				return effects;
			}

			return [];
		},
		async loadRankFeatures(nodesRef: CollectionReference): Promise<Feature[]> {
			const querySnapshot = await getDocs(query(nodesRef, orderBy("sortOrder")));
			const features = [];

			for (const doc of querySnapshot.docs) {
				const feature = doc.data() as Feature;

				// Load and sort sub-features based on sortOrder
				feature.subFeatures = (await Promise.all(
					feature.subFeatureIds.map(subFeatureId => this.getFeatureById(nodesRef, subFeatureId))
				)).sort((a, b) => a.sortOrder - b.sortOrder);

				// Load effects for the feature
				await this.loadEffects(feature);

				// Recursively load sub-sub-features and effects
				for (const subFeature of feature.subFeatures) {
					await this.loadEffects(subFeature);
					if (subFeature.subFeatureIds?.length > 0) {
						subFeature.subFeatures = (await Promise.all(
							subFeature.subFeatureIds.map(id => this.getFeatureById(nodesRef, id))
						)).sort((a, b) => a.sortOrder - b.sortOrder);
						for (const nested of subFeature.subFeatures) {
							await this.loadEffects(nested);
						}
					}
				}

				features.push(feature);
			}

			return features;
		}
	}
});